import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  VStack,
  Text,
  InputGroup,
  InputRightElement,
  IconButton,
  useToast,
  useClipboard,
  HStack,
  Box,
  Divider,
} from '@chakra-ui/react';
import { Copy, Check, UserPlus, Link } from 'lucide-react';
import { inviteMember } from '../lib/api';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function InviteModal({ isOpen, onClose, onSuccess }: InviteModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('editor');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const toast = useToast();
  const { hasCopied, onCopy } = useClipboard(inviteLink);

  const validateEmail = (value: string): boolean => {
    if (!value.trim()) {
      setEmailError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setEmailError('Enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validateEmail(email)) return;

    setIsSubmitting(true);
    try {
      const data = await inviteMember(email.trim(), role);
      toast({
        title: 'Invitation Sent',
        description: `${email} has been invited as ${role}.`,
        status: 'success',
        duration: 4000,
      });
      if (data?.invite_link) {
        setInviteLink(data.invite_link);
      } else {
        handleClose();
        onSuccess?.();
      }
    } catch (err: any) {
      toast({
        title: 'Invite Failed',
        description: err?.message || 'Could not send invitation',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setRole('editor');
    setEmailError('');
    setInviteLink('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl">
        <ModalHeader>
          <HStack spacing={2}>
            <UserPlus size={20} />
            <Text>Invite Team Member</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {inviteLink ? (
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Share this link with the new member. They can use it to sign up and join your organization.
              </Text>
              <InputGroup>
                <Input value={inviteLink} isReadOnly fontSize="sm" pr="3rem" />
                <InputRightElement>
                  <IconButton
                    aria-label="Copy link"
                    icon={hasCopied ? <Check size={16} /> : <Copy size={16} />}
                    size="sm"
                    variant="ghost"
                    onClick={onCopy}
                    colorScheme={hasCopied ? 'green' : 'gray'}
                  />
                </InputRightElement>
              </InputGroup>
              {hasCopied && (
                <Text fontSize="xs" color="green.500">Link copied to clipboard!</Text>
              )}
            </VStack>
          ) : (
            <VStack spacing={4}>
              <FormControl isInvalid={!!emailError}>
                <FormLabel fontSize="sm">Email Address</FormLabel>
                <Input
                  type="email"
                  placeholder="colleague@company.com"
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                    if (emailError) validateEmail(e.target.value);
                  }}
                  onBlur={() => email && validateEmail(email)}
                />
                <FormErrorMessage>{emailError}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Role</FormLabel>
                <Select value={role} onChange={e => setRole(e.target.value)}>
                  <option value="admin">Admin — Full access</option>
                  <option value="editor">Editor — Generate & review content</option>
                </Select>
              </FormControl>

              <Divider />

              <Box w="full">
                <HStack spacing={1} color="gray.500" fontSize="xs">
                  <Link size={12} />
                  <Text>
                    An email will be sent with a signup link. If Supabase email is not configured,
                    you'll get a shareable link instead.
                  </Text>
                </HStack>
              </Box>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            {inviteLink ? 'Done' : 'Cancel'}
          </Button>
          {!inviteLink && (
            <Button
              colorScheme="brand"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              leftIcon={<UserPlus size={16} />}
            >
              Send Invite
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
